/**
 * app-client-history.js — the client record's undo ring and the lists that
 * show it (App slice).
 *
 * Extracted from js/app-core.js (September 2026) for the same two reasons as
 * app-demo-client.js and name-pronunciation.js: app-core was 79 lines over its
 * audit-docs ceiling, and this feature had to change shape rather than merely
 * move. It used to read the QUOTES store, which nothing in the wizard ever
 * writes, so `quotes.find(q => q.id === this.activeClientId)` never matched an
 * id minted by autoSaveClient and the ⏮ button reported "No history yet" over
 * every client, forever. Snapshots were never written either: the one caller of
 * _addHistorySnapshot sat behind the same failed lookup. Inert end to end.
 *
 * All of these now address CLIENT_HISTORY by client id, which is what
 * activeClientId has always been.
 *
 * setClientQuoteStatus lives here for the same reason and not in app-quotes.js:
 * `history`, `quoteStatus` and `quoteStatusAt` are the three record-level fields
 * ClientStatus.CARRIED names, and this file owns the operations that write them.
 * (It also keeps app-quotes.js under its 1,800 line budget.)
 *
 * The CLIENT_HISTORY list renderers moved here in the same pass, for the same
 * budget reason and a better one: js/app-quotes.js crossed the 1,800-line
 * budget once this slice grew, and "the client record" is what these render.
 * `_clientRowHtml` is now the ONE row definition, and that claim is true for
 * the first time (September 2026). It was written down before it was so: the
 * comment said step 0 and step 6 both called it while step 6 carried its OWN
 * inline template, with no status badge, no HawkSoft chip, no action drawer,
 * and a Restore wired to a different method. Deleting that list (PF-11) took
 * the copy with it, and the dashboard Recent Clients card now calls this too.
 * So there is one row, on both surfaces that show a list of clients.
 *
 * NOT a plugin IIFE — this is an App slice (Object.assign; see the "App
 * Assembly" table in CLAUDE.md). It must load EAGERLY, after app-quotes.js:
 * every function here calls getClientHistory / saveClientHistory /
 * _clientVaultLocked, which that file owns.
 */
Object.assign(App, {

    // History cap: 3 snapshots per record. CLIENT_HISTORY is SecureStorage
    // encrypted AND cloud-synced (DOC_LOCAL_KEYS.clientHistory), capped at 200
    // entries, and every entry already carries a full form snapshot plus
    // mirrored drivers and vehicles. Each extra snapshot multiplies the WHOLE
    // blob on the wire, so this cap is a sync budget, not a disk one. It was 5
    // while it lived on the QUOTES store, where it never actually ran.
    HISTORY_CAP: 3,
    HISTORY_DEDUP_MS: 60_000,  // Ignore snapshot attempts within 1 min of the last one

    _addHistorySnapshot(record, { force = false } = {}) {
        if (!record || !record.data) return;
        if (!record.history) record.history = [];
        const latest = record.history[0];
        if (latest && !force) {
            const ageMs = Date.now() - new Date(latest.snapshotAt).getTime();
            if (ageMs < this.HISTORY_DEDUP_MS) return;
            if (JSON.stringify(latest.data) === JSON.stringify(record.data)) return;
        }
        record.history.unshift({
            snapshotAt: new Date().toISOString(),
            data: JSON.parse(JSON.stringify(record.data)),
        });
        if (record.history.length > this.HISTORY_CAP) {
            record.history.length = this.HISTORY_CAP;
        }
    },

    // Restore a snapshot. Pushes the current state as a fresh snapshot first
    // (force=true, bypass dedup) so the undo is itself undoable.
    async _restoreSnapshot(snapshotIndex) {
        if (!this.activeClientId) return;
        const clients = this.getClientHistory();
        const idx = clients.findIndex(c => c && c.id === this.activeClientId);
        if (idx < 0) return;
        const record = clients[idx];
        if (!record.history || !record.history[snapshotIndex]) return;
        const snapshot = record.history[snapshotIndex];
        // Save current state into history BEFORE restoring, so the restore is reversible
        const currentAsRecord = { ...record, data: JSON.parse(JSON.stringify(this.data)) };
        this._addHistorySnapshot(currentAsRecord, { force: true });
        record.history = currentAsRecord.history;
        // Overwrite record.data with snapshot and persist
        record.data = JSON.parse(JSON.stringify(snapshot.data));
        record.data._clientId = this.activeClientId;
        // CLIENT_HISTORY entries are stamped savedAt, not updatedAt — that is the
        // field _clientRowHtml and the cap read.
        record.savedAt = new Date().toISOString();
        // Read the boolean. A refused write (locked vault, quota) followed by an
        // unconditional _switchToClient would reload the PRE-restore entry off
        // disk and lose the undo point we just pushed, which is the one thing a
        // restore must never do.
        if (!this.saveClientHistory(clients)) {
            this.toast('Could not save. Unlock the vault and try again.', { type: 'error' });
            return;
        }
        // Re-apply the restored data to the live form
        await this._switchToClient(record);
        this.toast('↩️ Restored to ' + new Date(snapshot.snapshotAt).toLocaleTimeString());
    },

    // History modal — opens from the badge's ⏮ button. Shows up to HISTORY_CAP
    // snapshots with relative timestamps; click Restore to revert the form.
    async _showHistoryModal() {
        if (!this.activeClientId) {
            this.toast('⚠️ No active client, nothing to restore');
            return;
        }
        // A locked vault makes getClientHistory() return [], so "No history yet"
        // would be a lie about a record whose undo ring is sitting encrypted on
        // disk, and its follow-on sentence tells the agent to make MORE edits,
        // the opposite of what they should do. Same rule as the client list
        // (renderStep0ClientHistory) and setClientStatus.
        // Skip the read entirely rather than reading then re-checking: there is
        // nothing to decrypt and the three-way state stays honest.
        const locked = (typeof this._clientVaultLocked === 'function') && this._clientVaultLocked();
        const clients = locked ? [] : this.getClientHistory();
        const record = clients.find(c => c && c.id === this.activeClientId);
        const history = (record && record.history) || [];
        const modal = document.createElement('div');
        modal.className = 'modal-overlay active';
        modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
        let bodyHtml;
        if (locked) {
            bodyHtml = '<p class="hint chist-notice">🔒 Client vault locked, snapshots appear after unlock</p>';
        } else if (!history.length) {
            bodyHtml = '<p style="color:var(--text-secondary);">No history yet. Snapshots are captured automatically as you work. Make more edits and save to build up history.</p>';
        } else {
            bodyHtml = '<div class="history-list">' + history.map((h, i) => {
                const when = new Date(h.snapshotAt).toLocaleString();
                const filled = Object.keys(h.data || {}).filter(k => {
                    const v = h.data[k];
                    return v !== null && v !== undefined && v !== '' && !String(k).startsWith('_');
                }).length;
                return `<div class="history-row">
                    <div class="history-info">
                        <div class="history-when">${when}</div>
                        <div class="history-meta">${filled} fields captured</div>
                    </div>
                    <button class="modal-btn modal-btn-primary" data-history-idx="${i}">Restore</button>
                </div>`;
            }).join('') + '</div>';
        }
        modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <span class="modal-icon">⏮</span>
                    <h2 class="modal-title">Recent snapshots</h2>
                </div>
                <div class="modal-body">
                    <p style="color:var(--text-secondary);font-size:13px;margin-bottom:12px;">
                        Automatic snapshots capture the form state roughly once a minute as you work. Restoring is itself undoable: your current state is saved before the restore.
                    </p>
                    ${bodyHtml}
                </div>
                <div class="modal-actions">
                    <button class="modal-btn modal-btn-secondary" id="history-close">Close</button>
                </div>
            </div>`;
        document.body.appendChild(modal);
        modal.querySelector('#history-close').onclick = () => modal.remove();
        modal.querySelectorAll('[data-history-idx]').forEach(btn => {
            btn.onclick = async () => {
                const idx = parseInt(btn.dataset.historyIdx, 10);
                if (!confirm('Restore the form to this snapshot? Your current state will be saved as an undo point first.')) return;
                modal.remove();
                await this._restoreSnapshot(idx);
            };
        });
    },
    /**
     * Set the pipeline status of the CLIENT record on screen. Returns true when
     * written. A sibling of setQuoteStatus, not a rename: that one addresses the
     * QUOTES library by QUOTE id (the library card still wires it), this one
     * CLIENT_HISTORY by CLIENT id, and the export picker only ever holds the
     * latter — handing it a client id is what made "Mark as Quoted" a no-op that
     * closed the sheet and said nothing. Shares the frozen QUOTE_STATUSES
     * vocabulary, which is NOT ClientStatus's: both own a 'sold', and filing a
     * client removes it from the working lists where marking a quote sold must
     * not. See js/client-status.js.
     */
    setClientQuoteStatus(id, status) {
        const key = String(status || '').trim().toLowerCase();
        if (!this.QUOTE_STATUSES.some(s => s.key === key)) return false;
        // The READ is the hazard: a locked vault makes getClientHistory() return
        // [], so the lookup below would report "no such client" about a client
        // that plainly exists. Same guard as setClientStatus.
        if (this._clientVaultLocked()) {
            this.toast('🔒 Unlock the vault to update this quote', { type: 'error', dedupe: true });
            return false;
        }
        const clients = this.getClientHistory();
        const idx = clients.findIndex(c => c && c.id === id);
        if (idx < 0) return false;
        // Mutate in place. No splice + unshift: re-inserting at the head rewrites
        // recency order and could push a DIFFERENT client past the 200 cap.
        // safeSave schedules the cloud push itself; there is no second one here.
        clients[idx].quoteStatus = key;
        clients[idx].quoteStatusAt = new Date().toISOString();
        if (this.saveClientHistory(clients) === false) return false;
        this.renderStep0ClientHistory();
        const meta = this.QUOTE_STATUSES.find(s => s.key === key);
        this.toast(`${meta.icon} Marked ${meta.label.toLowerCase()}`);
        return true;
    },


    /**
     * The "Editing: <name>" strip and its tel:/mailto: chips — the second
     * surface that shows the active client record, after the step-0 list
     * below. Moved out of js/app-core.js (September 2026) with the list, both
     * because the record is what it renders and because app-core sat 78 lines
     * over its frozen ceiling.
     *
     * Callers are unchanged: this is the same App object. js/app-core.js
     * calls it through a `typeof === 'function'` guard on the save-back
     * path, so a harness that evals app-core alone still runs.
     */
    // Badge rendering — shows "Editing: [Client Name] · [status]" below the header.
    // Hidden when no active record (blank form / fresh start).
    _updateActiveClientBadge() {
        const badge = document.getElementById('activeClientBadge');
        if (!badge) return;
        if (!this.activeClientId) {
            badge.style.display = 'none';
            return;
        }
        const first = (this.data.firstName || '').trim();
        const last = (this.data.lastName || '').trim();
        const name = [first, last].filter(Boolean).join(' ') || 'Untitled Client';
        const status = this._dirty ? 'unsaved changes' : 'saved';
        const statusClass = this._dirty ? 'acb-status-dirty' : 'acb-status-clean';
        const nameEl = document.getElementById('acbName');
        const statusEl = document.getElementById('acbStatus');
        if (nameEl) nameEl.textContent = name;
        if (statusEl) {
            statusEl.textContent = status;
            statusEl.className = 'acb-status ' + statusClass;
        }
        this._renderBadgeContacts();
        this._renderBadgeLineOfBusiness();
        badge.style.display = '';
    },

    /**
     * Marks which of the three line-of-business buttons is the live one, from
     * data.qType. Read-only: the buttons call App.setLineOfBusiness, which owns
     * the confirm and the handleType call.
     *
     * An unrecognised or missing qType leaves all three unmarked rather than
     * guessing 'both'. A control that says "Both" over a form that is not both
     * is worse than one that says nothing, because the next click is then a
     * no-op the agent reads as broken.
     */
    _renderBadgeLineOfBusiness() {
        const group = document.getElementById('acbLob');
        if (!group) return;
        const live = (this.data && this.data.qType) || '';
        group.querySelectorAll('[data-lob]').forEach((btn) => {
            const on = btn.dataset.lob === live;
            btn.classList.toggle('is-active', on);
            btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
    },

    // Contact chips in the Editing banner — the phone/email of the record on
    // screen, so an agent on step 4 can call or email without navigating back
    // to step 1. Each chip hides when its value is blank; the wrapper hides
    // when both are. Rides every existing _updateActiveClientBadge() trigger,
    // including the 250 ms _scheduleSaveUiRefresh debounce, so typing a phone
    // number updates the chip without a new call site.
    //
    // Utils.safeUrl() is deliberately NOT used here: it allows http:/https:
    // ONLY and returns '' for tel:/mailto: (js/utils.js, pinned by
    // tests/utils.test.js). Sanitize per-scheme instead — digits/+ for tel
    // (the js/prospect.js _safeTelHref rule), and a strict single-token
    // address for mailto that rejects whitespace, quotes, angle brackets,
    // commas, semicolons and backslashes, so nothing can smuggle in a scheme
    // or a header break.
    _renderBadgeContacts() {
        const wrap = document.getElementById('acbContacts');
        if (!wrap) return;

        const rawPhone = String(this.data.phone || '').trim();
        const stripped = rawPhone.replace(/[^\d+]/g, '');
        // Needs at least one actual digit — a field holding only punctuation
        // ("+", "n/a") would otherwise render a chip nobody can dial.
        const telDigits = /\d/.test(stripped) ? stripped : '';
        const rawEmail = String(this.data.email || '').trim();
        const email = /^[^\s@<>"',;:\\]+@[^\s@<>"',;:\\]+\.[^\s@<>"',;:\\]+$/.test(rawEmail) ? rawEmail : '';

        const setChip = (linkId, textId, href, text, title) => {
            const a = document.getElementById(linkId);
            const t = document.getElementById(textId);
            if (!a || !t) return;
            if (href) {
                t.textContent = text;
                a.setAttribute('href', href);
                a.setAttribute('title', title);
                a.style.display = '';
            } else {
                t.textContent = '';
                a.removeAttribute('href');
                a.removeAttribute('title');
                a.style.display = 'none';
            }
        };

        // applyData() already normalizes stored phones, but re-format
        // defensively — data can also arrive from a sync pull or an AI fill.
        const phoneText = this._fmtPhoneVal(rawPhone) || rawPhone;
        setChip('acbPhone', 'acbPhoneText', telDigits ? 'tel:' + telDigits : '', phoneText, 'Call ' + phoneText);
        setChip('acbEmail', 'acbEmailText', email ? 'mailto:' + email : '', email, 'Email ' + email);

        wrap.style.display = (telDigits || email) ? '' : 'none';
    },


    // ── The list that shows the record ────────────────────────────────
    // Moved out of js/app-quotes.js (September 2026): that file crossed the
    // 1,800-line budget once the switch guard grew, and a list of client
    // records belongs with the record. The search caret helper, the menu-open
    // id and the show-all flags stay in app-quotes.js — same App object, so
    // every call below resolves unchanged.
    //
    // ONE row, two surfaces: the step-0 Resume list here, and the dashboard's
    // Recent Clients card (js/dashboard-widgets.js), which reads this at CALL
    // time and falls back to its own plain row if App has not loaded.
    //
    // `showMenu` opens the action drawer INSIDE the card rather than as an
    // absolutely positioned popover: the expanded list is an `overflow-y: auto`
    // scroller, which would clip a popover outright (the .vm-row-actions lesson
    // in CLAUDE.md).
    //
    // `opts` carries the only two things that genuinely differ by surface, and
    // both default to the step-0 behaviour so the existing call site is
    // unchanged:
    //   opts.restore — the ONE awaited method the Restore button calls. Step 0
    //     is already inside the wizard so it advances (restoreClientAndContinue,
    //     A-056); the dashboard has to open the tool first and must not advance
    //     (openClientInQuoting, A-005). Both decide for themselves whether to
    //     move, and neither is ever two statements in an onclick.
    //   opts.menu — false drops the ⋯ button entirely. The drawer it opens
    //     re-renders the step-0 list, so on any other surface the button would
    //     be a control that appears to do nothing.
    _clientRowHtml(c, showMenu, opts) {
        const o = opts || {};
        // `restore` lands raw in an onclick, and _clientRowHtml is reachable on
        // window.App, so it is shape-checked before it gets there — the same
        // rule as allowlisting a caller-supplied key before writing it into
        // quote data. A dotted identifier path is all either call site needs;
        // anything else falls back to the default rather than breaking out of
        // the attribute.
        const restore = /^[A-Za-z_$][\w$]*(\.[A-Za-z_$][\w$]*)*$/.test(o.restore || '')
            ? o.restore : 'App.restoreClientAndContinue';
        const when = new Date(c.savedAt);
        const dateTime = when.toLocaleDateString() + ' · ' + when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        // A-066 shape: a malformed row (partial cloud merge, corrupt JSON that
        // parsed to `{id}`) must cost that row, not blank the whole list.
        const d = c.data || {};
        const addr = [d.addrCity, d.addrState].filter(Boolean).join(', ');
        const id = this._jsArg(c.id);
        const badge = window.ClientStatus ? window.ClientStatus.badge(c) : null;
        const filed = !!badge;
        // The client on screen right now (step-0 list only: opts.open). Its
        // button says Continue and carries on to step 1; every other row says
        // Open, the same word the office rows use. It used to say Restore,
        // which on the open client reloaded it and stayed on step 0.
        const isOpen = !!o.open;
        // The badge is its OWN flex child, never inside .chist-name or
        // .chist-meta: both are `text-overflow: ellipsis`, so a long name would
        // truncate away the one thing that says why the row is here.
        const badgeHtml = badge
            ? `<span class="chist-badge ${badge.className}" title="${Utils.escapeAttr(this._filedOnText(c))}">${this.escapeHTML(badge.label)}</span>`
            : '';
        // The HawkSoft join, when App.setClientHawksoftNumber has written one.
        // HawkSoftLink is read at CALL time and returns a DESCRIPTOR, never
        // HTML, so escaping stays here; a missing module degrades to no chip
        // rather than breaking the row. Its own flex child for the same reason
        // as the badge above — the two ellipsis columns would eat it.
        const hs = (window.HawkSoftLink && d.hawksoftClientNumber)
            ? window.HawkSoftLink.clientUrl(d.hawksoftClientNumber, { subject: 'client' })
            : null;
        const hsHtml = hs
            ? `<a class="chist-hs-chip" href="${Utils.escapeAttr(hs.href)}" title="${Utils.escapeAttr(hs.title)}"`
              + `${hs.target ? ` target="${hs.target}" rel="${Utils.escapeAttr(hs.rel)}"` : ''}>HS #${this.escapeHTML(String(d.hawksoftClientNumber))}</a>`
            : '';
        const menuHtml = showMenu ? `
                    <div class="chist-drawer">
                        ${filed
                            ? `<button type="button" class="chist-drawer-btn" onclick="App.setClientStatus(${id}, 'active')">↩️ Reopen</button>`
                            : `<button type="button" class="chist-drawer-btn" onclick="App.setClientStatus(${id}, 'sold')">💰 Mark sold</button>
                               <button type="button" class="chist-drawer-btn" onclick="App.setClientStatus(${id}, 'archived')">🗄 Archive</button>`}
                        <button type="button" class="chist-drawer-btn chist-drawer-danger" onclick="App.deleteClientFromHistory(${id})">🗑 Delete</button>
                    </div>` : '';
        return `<div class="chist-row-wrap${filed ? ' is-filed' : ''}${isOpen ? ' is-open' : ''}">
                <div class="chist-row"${filed ? ` data-chist-status="${Utils.escapeAttr(window.ClientStatus.statusOf(c))}"` : ''}>
                    ${o.agent && window.AgencyIntakes && AgencyIntakes.agentPillHtml ? AgencyIntakes.agentPillHtml(o.agent) : ''}
                    <div class="chist-info">
                        <span class="chist-name">${this.escapeHTML(c.name || 'Unnamed Client')}</span>
                        <span class="chist-meta">${this.escapeHTML(c.summary)}${addr ? ' • ' + this.escapeHTML(addr) : ''} • ${dateTime}</span>
                    </div>
                    ${isOpen ? '<span class="chist-open-pill">Open now</span>' : ''}
                    ${badgeHtml}
                    ${window.AgencyIntakes ? AgencyIntakes.rowChipHtml('personal', c.id) : ''}
                    ${hsHtml}
                    <div class="chist-actions">
                        <button class="btn btn-primary btn-sm" onclick="${restore}(${id})">${isOpen ? 'Continue' : 'Open'}</button>
                        ${o.menu === false ? '' : `<button class="chist-menu-btn${showMenu ? ' is-open' : ''}" title="Sold, archive or delete" aria-label="More actions" aria-expanded="${showMenu ? 'true' : 'false'}" onclick="App.toggleClientMenu(${id})">⋯</button>`}
                    </div>
                </div>${menuHtml}
            </div>`;
    },

    // "Sold on 3/9/2026" for the badge tooltip, or just the label when an older
    // entry carries a status with no timestamp.
    _filedOnText(c) {
        const badge = window.ClientStatus ? window.ClientStatus.badge(c) : null;
        if (!badge) return '';
        const at = c && c.statusAt;
        if (!at) return badge.label;
        const when = new Date(at);
        return isNaN(when.getTime()) ? badge.label : `${badge.label} on ${when.toLocaleDateString()}`;
    },

    // Active / Sold / Archived / All. Rendered once something has actually been
    // filed: a fresh install must not grow a row of chips that all read zero.
    //
    // The `view !== 'active'` half is load-bearing. Reopening the LAST filed
    // client while standing in the Archived view takes the filed count to zero,
    // and on a counts-only condition the chips would vanish underneath the agent
    // leaving them in an empty view with no control to get back out of it.
    _clientFilterChipsHtml(counts) {
        const view = this._step0View || 'active';
        if (!counts || ((counts.sold + counts.archived) === 0 && view === 'active')) return '';
        const chips = [
            ['active', 'Active', counts.active],
            ['sold', 'Sold', counts.sold],
            ['archived', 'Archived', counts.archived],
            ['all', 'All', counts.all],
        ];
        return `<div class="chist-filters" role="group" aria-label="Filter clients">${chips.map(([key, label, n]) =>
            `<button type="button" class="chist-filter${view === key ? ' is-active' : ''}" data-chist-view="${key}" aria-pressed="${view === key ? 'true' : 'false'}" onclick="App.setStep0ClientView('${key}')">${label}<span class="chist-filter-n">${n}</span></button>`
        ).join('')}</div>`;
    },

    // Says which view is empty, and where the rest of the clients went. "No
    // clients match your search" over an Active view that is empty because
    // everything is archived sends the agent hunting for a bug.
    _emptyClientViewText(view, searchTerm, counts) {
        if (searchTerm) return 'No clients match your search';
        if (view === 'sold') return 'No clients marked sold yet';
        if (view === 'archived') return 'Nothing archived yet';
        const filed = (counts.sold || 0) + (counts.archived || 0);
        return filed
            ? `No active clients. ${filed} filed as sold or archived.`
            : 'No previous clients yet';
    },

    /**
     * Show or hide the whole Resume card. It cannot be decided in the markup,
     * which has no way to know whether there are clients.
     *
     * A LOCKED VAULT IS NOT AN EMPTY LIST. `_clientVaultLocked()` true means
     * "we cannot see them", and the lock notice is the only thing on screen
     * telling the agent why their clients are missing — hiding the card there
     * would delete the explanation along with the heading. Same rule as
     * computeComplianceSummary's `unavailable` versus a measured zero.
     */
    _setStep0ResumeVisible(visible) {
        const card = document.getElementById('step0ResumeCard');
        if (!card) return;
        card.style.display = visible ? '' : 'none';
    },

    _step0Agent: '',

    setStep0Agent(label) {
        const v = String(label || '').toUpperCase();
        this._step0Agent = /^[A-Z]{1,4}$/.test(v) ? v : '';
        this._step0ShowAll = false;
        this._openClientMenuId = null;
        this.renderStep0ClientHistory();
        try {
            const chip = document.querySelector(`#step0ClientHistoryList [data-step0-agent="${this._step0Agent}"]`);
            if (chip) chip.focus();
        } catch (_) { /* focus is a nicety */ }
    },

    /**
     * The step-0 client list: this agent's Client History AND the intakes
     * colleagues saved to the office (js/agency-intakes.js), in one list, newest
     * first, each row wearing the initials of whoever took it. Search covers
     * name, city, line and initials; the chips filter by status and by person.
     *
     * A list with nothing in it (no client here, none from the office, vault
     * open) hides the card, the old first-run rule; anything to show brings it
     * back, including a colleague's intake on a computer with no clients yet.
     */
    renderStep0ClientHistory() {
        const container = document.getElementById('step0ClientHistoryList');
        if (!container) return;
        // The office half and the pills live in the lazy js/agency-intakes.js;
        // without it this is the agent's own list, as before.
        const AI = window.AgencyIntakes && typeof AgencyIntakes.agentLabel === 'function' ? AgencyIntakes : null;
        if (AI) AI.init();
        const locked = this._clientVaultLocked();
        const allClients = this.getClientHistory();
        const view = this._step0View || 'active';
        const counts = this._clientStatusCounts(allClients);
        const office = AI && (view === 'active' || view === 'all') && AI.isReady() ? AI.officeOnly('personal') : [];
        const label = (who) => (AI ? AI.agentLabel(who) : '');

        const rows = this._visibleClients(allClients, view).map(c => ({ c, at: Date.parse(c.savedAt) || 0, who: AI ? AI.whoTookClient(c.id) : null }));
        office.forEach(item => rows.push({ office: item, at: Date.parse(item.updatedAt) || 0, who: AI.takenBy('personal', item.id) || { name: item.createdName || '' } }));
        // The client on screen first (it reads "Open now" / Continue), then newest.
        const openId = this.activeClientId || null;
        const isOpenRow = (r) => (openId && r.c && r.c.id === openId ? 1 : 0);
        rows.sort((a, b) => (isOpenRow(b) - isOpenRow(a)) || (b.at - a.at));

        const agentCounts = {};
        rows.forEach(r => { const l = label(r.who); if (l) agentCounts[l] = (agentCounts[l] || 0) + 1; });
        if (this._step0Agent && !agentCounts[this._step0Agent]) this._step0Agent = '';
        const agent = this._step0Agent || '';
        const byAgent = agent ? rows.filter(r => label(r.who) === agent) : rows;

        const searchTerm = (this._step0Search || '').trim().toLowerCase();
        const filtered = searchTerm ? byAgent.filter(r => {
            const ini = label(r.who).toLowerCase();
            const who = String((r.who && r.who.name) || '').toLowerCase();
            if (r.office) {
                const s = r.office.summary || {};
                return [s.name, s.city, s.state, s.line, ini, who].some(v => String(v || '').toLowerCase().includes(searchTerm));
            }
            const c = r.c; const d = c.data || {};
            return [c.name, d.addrCity, d.addrState, c.summary, d.hawksoftClientNumber, ini, who]
                .some(v => String(v || '').toLowerCase().includes(searchTerm));
        }) : byAgent;

        const total = rows.length;
        this._setStep0ResumeVisible(!!(total || locked || allClients.length || searchTerm));
        const showAll = this._step0ShowAll;
        const PREVIEW = 6;
        const displayed = (showAll || searchTerm) ? filtered : filtered.slice(0, PREVIEW);
        // What the list below shows, the same number "View All N" uses. It
        // counted every saved client, sold and archived too, so it read 46
        // over a list of 45.
        const officeShown = filtered.filter(r => r.office).length;
        const officeNote = officeShown ? `, ${officeShown} from the office` : '';

        let html = `<div class="step0-block-title">${filtered.length} ${filtered.length === 1 ? 'client' : 'clients'}${officeNote}</div>`;
        if (locked && !allClients.length) {
            html += '<div class="hint chist-notice">🔒 Client vault locked, your saved clients appear after unlock</div>';
        }
        // Status and who-took-it chips share one wrapping row.
        const chipRow = this._clientFilterChipsHtml(counts) + (AI ? AI.agentChipsHtml(agentCounts, total, agent) : '');
        if (chipRow) html += `<div class="chist-filter-row">${chipRow}</div>`;

        // Search shows once the list holds more than fits (or one is typed).
        if (total > PREVIEW || searchTerm) {
            html += `<div class="ch-search-bar">
                <input type="search" class="ch-search-input" data-no-autosave placeholder="Search by name, city, line or initials…" aria-label="Search clients" value="${Utils.escapeAttr(this._step0Search || '')}" oninput="App._step0Search=this.value;App._scheduleClientSearchRender();">
            </div>`;
        }

        if (!total && !searchTerm) {
            if (!(locked && !allClients.length)) {
                html += `<div class="hint chist-notice">${this.escapeHTML(view === 'active' && !(counts.sold + counts.archived)
                    ? 'No clients yet. Intakes you and your team save show up here.'
                    : this._emptyClientViewText(view, '', counts))}</div>`;
            }
        } else if (!displayed.length) {
            html += `<div class="ch-no-results">${this.escapeHTML(this._emptyClientViewText(view, searchTerm || agent, counts))}</div>`;
        } else {
            const listClass = (showAll || searchTerm) ? 'ch-list-expanded' : '';
            html += `<div class="${listClass}">`;
            html += displayed.map(r => (r.office
                ? AI.officeRowHtml(r.office, r.who)
                : this._clientRowHtml(r.c, this._openClientMenuId === r.c.id, { agent: r.who, open: !!openId && r.c.id === openId }))).join('');
            html += '</div>';
        }

        if (filtered.length > PREVIEW && !searchTerm) {
            html += showAll
                ? `<div class="chist-more-row"><button class="ch-view-all-btn" onclick="App._step0ShowAll=false;App.renderStep0ClientHistory();">Show Less ▲</button></div>`
                : `<div class="chist-more-row"><button class="ch-view-all-btn" onclick="App._step0ShowAll=true;App.renderStep0ClientHistory();">View All ${filtered.length} Clients ▼</button></div>`;
        }

        this._preserveSearchCaret(container, () => { container.innerHTML = html; });
        const stale = document.getElementById('step0OfficeIntakes'); // the old separate group
        if (stale) stale.remove();
    },

});
